import React, { useState, useEffect, useCallback, useRef } from 'react';
import secureStorage from '../utils/secureStorage';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  ArrowLeft,
  ChevronRight,
  Edit,
  GripVertical,
  Layers,
  Loader2,
  Package,
  PanelRightClose,
  PanelRightOpen,
  Plane,
  Plus,
  Printer,
  Save,
  Search,
  Trash2,
  UserMinus,
  UserPlus,
  Users,
  X
} from 'lucide-react';
import { API_BASE_URL } from '../config';
import { writeSanitizedHTML } from '../utils/safePrint';

const getAuthHeaders = () => {
  const token = secureStorage.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const emptyForce = {
  name: '', leader_name: '', leader_contact: '',
  component_list: [],
  personnel_roster: [],
  check_in_location: '', check_in_time: '', resource_status: 'Available', current_location: '',
  operational_period_start: '', operational_period_end: '',
  mission_assignment: '', comms_plan: '', safety_notes: '',
  requisition_order_number: '', fuel_support_needs: '', check_out_time: ''
};

const statusBadgeClass = (status) => {
  const map = { 'Assigned': 'bg-blue-500/20 text-blue-400', 'In Transit': 'bg-yellow-500/20 text-yellow-400', 'Released': 'bg-green-500/20 text-green-400' };
  return map[status] || 'bg-gray-500/20 text-gray-400';
};

const TaskForcesModule = ({ currentExercise, theme }) => {
  const API = API_BASE_URL + '/api';
  const [forces, setForces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [editingForce, setEditingForce] = useState(null);
  const [formData, setFormData] = useState({ ...emptyForce });
  const [participants, setParticipants] = useState([]);
  const [resources, setResources] = useState([]);
  const [aircraft, setAircraft] = useState([]); // Fixed Wing / Helicopters / Drones from ICS 220
  const [saving, setSaving] = useState(false);
  const [newComponent, setNewComponent] = useState({ description: '', unit_identifier: '', quantity: '' });
  const [newPersonnel, setNewPersonnel] = useState({ name: '', role: '', agency: '', qualifications: '' });

  // Quick Assign state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarTab, setSidebarTab] = useState('participants');
  const [searchQuery, setSearchQuery] = useState('');
  const [dropTargetId, setDropTargetId] = useState(null);
  const [assigningId, setAssigningId] = useState(null);
  const [toast, setToast] = useState(null);

  const toastTimer = useRef(null);
  const showToast = (message, type = 'success') => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ message, type });
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  };

  const fetchForces = useCallback(async () => {
    if (!currentExercise?.id) return;
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/task-forces?exercise_id=${currentExercise.id}`, { headers });
      setForces(response.data);
    } catch (error) {
      console.error('Error fetching task forces:', error);
    } finally {
      setLoading(false);
    }
  }, [currentExercise?.id, API]);

  const fetchParticipants = useCallback(async () => {
    if (!currentExercise?.id) return;
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/exercises/${currentExercise.id}/participants`, { headers });
      setParticipants(response.data || []);
    } catch (error) {
      console.error('Error fetching participants:', error);
    }
  }, [currentExercise?.id, API]);

  const fetchResources = useCallback(async () => {
    if (!currentExercise?.id) return;
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/resources?exercise_id=${currentExercise.id}`, { headers });
      const allResources = response.data || [];
      const allItems = [];
      for (const res of allResources) {
        try {
          const unitsRes = await axios.get(`${API}/resources/${res.id}/units`, { headers });
          const units = unitsRes.data || [];
          if (units.length > 0) {
            units.forEach(u => allItems.push({ ...u, resource_type: res.resource_type, parent_resource_id: res.id, is_parent: false }));
          } else {
            // Parent-only fallback so newly added resources with no sub-units still appear
            allItems.push({
              id: res.id,
              unit_name: res.resource_type || res.identification || 'Unnamed Resource',
              resource_type: res.resource_type,
              status: res.status || 'Available',
              task_force: res.task_force || '',
              parent_resource_id: res.id,
              is_parent: true,
            });
          }
        } catch (unitsErr) {
          console.warn(`[TaskForcesModule] units fetch failed for resource ${res.id}:`, unitsErr?.message || unitsErr);
        }
      }
      setResources(allItems);
    } catch (error) {
      console.error('Error fetching resources:', error);
    }
  }, [currentExercise?.id, API]);

  const fetchAircraft = useCallback(async () => {
    if (!currentExercise?.id) return;
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/ics-220?exercise_id=${currentExercise.id}`, { headers });
      const summaries = response.data || [];
      const flat = [];
      summaries.forEach(s => {
        (s.fixed_wing_aircraft || []).forEach(a => flat.push({ ...a, summary_id: s.id, category: 'fixed_wing' }));
        (s.helicopters || []).forEach(a => flat.push({ ...a, summary_id: s.id, category: 'helicopter' }));
        (s.drones || []).forEach(a => flat.push({ ...a, summary_id: s.id, category: 'drone' }));
      });
      setAircraft(flat);
    } catch (error) {
      console.error('Error fetching ICS 220 aircraft:', error);
    }
  }, [currentExercise?.id, API]);

  useEffect(() => {
    fetchForces();
    fetchParticipants();
    fetchResources();
    fetchAircraft();
  }, [fetchForces, fetchParticipants, fetchResources, fetchAircraft]);

  // Quick Assign - Participant
  const assignParticipant = async (participantId, forceName) => {
    setAssigningId(participantId);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      await axios.patch(`${API}/participants/${participantId}/assign`, { taskForce: forceName }, { headers });
      setParticipants(prev => prev.map(p => p.id === participantId ? { ...p, taskForce: forceName } : p));
      showToast(`Participant assigned to ${forceName}`);
    } catch (error) {
      showToast('Failed to assign participant', 'error');
    } finally {
      setAssigningId(null);
      setDropTargetId(null);
    }
  };

  const unassignParticipant = async (participantId) => {
    setAssigningId(participantId);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      await axios.patch(`${API}/participants/${participantId}/assign`, { taskForce: '' }, { headers });
      setParticipants(prev => prev.map(p => p.id === participantId ? { ...p, taskForce: '' } : p));
      showToast('Participant unassigned');
    } catch (error) {
      showToast('Failed to unassign', 'error');
    } finally {
      setAssigningId(null);
    }
  };

  // Quick Assign - Resource
  const assignResource = async (resourceId, forceName) => {
    setAssigningId(resourceId);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const item = resources.find(r => r.id === resourceId);
      const endpoint = item?.is_parent
        ? `${API}/resources/${resourceId}/assign`
        : `${API}/resource-units/${resourceId}/assign`;
      await axios.patch(endpoint, { task_force: forceName }, { headers });
      setResources(prev => prev.map(r => r.id === resourceId ? { ...r, task_force: forceName } : r));
      showToast(item?.is_parent ? `Resource assigned to ${forceName}` : `Unit assigned to ${forceName}`);
    } catch (error) {
      showToast('Failed to assign resource', 'error');
    } finally {
      setAssigningId(null);
      setDropTargetId(null);
    }
  };

  const unassignResource = async (resourceId) => {
    setAssigningId(resourceId);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const item = resources.find(r => r.id === resourceId);
      const endpoint = item?.is_parent
        ? `${API}/resources/${resourceId}/assign`
        : `${API}/resource-units/${resourceId}/assign`;
      await axios.patch(endpoint, { task_force: '' }, { headers });
      setResources(prev => prev.map(r => r.id === resourceId ? { ...r, task_force: '' } : r));
      showToast('Resource unassigned');
    } catch (error) {
      showToast('Failed to unassign', 'error');
    } finally {
      setAssigningId(null);
    }
  };

  // Quick Assign - Aircraft (fixed wing / helicopter / drone from ICS 220)
  const aircraftKey = (a) => `air-${a.summary_id}-${a.category}-${a.id}`;

  const assignAircraft = async (aircraftItem, forceName) => {
    const key = aircraftKey(aircraftItem);
    setAssigningId(key);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      await axios.patch(
        `${API}/ics-220/${aircraftItem.summary_id}/aircraft/${aircraftItem.id}/assign`,
        { category: aircraftItem.category, task_force: forceName },
        { headers }
      );
      setAircraft(prev => prev.map(a =>
        (a.summary_id === aircraftItem.summary_id && String(a.id) === String(aircraftItem.id) && a.category === aircraftItem.category)
          ? { ...a, task_force: forceName } : a
      ));
      showToast(`Aircraft assigned to ${forceName}`);
    } catch (error) {
      showToast('Failed to assign aircraft', 'error');
    } finally {
      setAssigningId(null);
      setDropTargetId(null);
    }
  };

  const unassignAircraft = async (aircraftItem) => {
    const key = aircraftKey(aircraftItem);
    setAssigningId(key);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      await axios.patch(
        `${API}/ics-220/${aircraftItem.summary_id}/aircraft/${aircraftItem.id}/assign`,
        { category: aircraftItem.category, task_force: '' },
        { headers }
      );
      setAircraft(prev => prev.map(a =>
        (a.summary_id === aircraftItem.summary_id && String(a.id) === String(aircraftItem.id) && a.category === aircraftItem.category)
          ? { ...a, task_force: '' } : a
      ));
      showToast('Aircraft unassigned');
    } catch (error) {
      showToast('Failed to unassign', 'error');
    } finally {
      setAssigningId(null);
    }
  };

  // Drag handlers
  const onDragStart = (e, item, itemType) => {
    if (itemType === 'aircraft') {
      e.dataTransfer.setData('item_id', JSON.stringify({ summary_id: item.summary_id, aircraft_id: item.id, category: item.category }));
    } else {
      e.dataTransfer.setData('item_id', item.id);
    }
    e.dataTransfer.setData('item_type', itemType);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e, forceId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetId(forceId);
  };

  const onDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setDropTargetId(null);
  };

  const onDrop = (e, forceName) => {
    e.preventDefault();
    const itemId = e.dataTransfer.getData('item_id');
    const itemType = e.dataTransfer.getData('item_type');
    if (itemId) {
      if (itemType === 'resource') {
        assignResource(itemId, forceName);
      } else if (itemType === 'aircraft') {
        try {
          const { summary_id, aircraft_id, category } = JSON.parse(itemId);
          const aircraftItem = aircraft.find(a => a.summary_id === summary_id && String(a.id) === String(aircraft_id) && a.category === category);
          if (aircraftItem) assignAircraft(aircraftItem, forceName);
        } catch (err) { console.error('Bad aircraft drag payload', err); }
      } else {
        assignParticipant(itemId, forceName);
      }
    }
    setDropTargetId(null);
  };

  // CRUD handlers
  const handleSave = async () => {
    if (!formData.name.trim()) return;
    setSaving(true);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const payload = { ...formData, exercise_id: currentExercise.id };
      if (editingForce) {
        await axios.put(`${API}/task-forces/${editingForce.id}`, payload, { headers });
        showToast(`Task Force "${payload.name}" updated`);
      } else {
        await axios.post(`${API}/task-forces`, payload, { headers });
        showToast(`Task Force "${payload.name}" created`);
      }
      setView('list');
      setEditingForce(null);
      setFormData({ ...emptyForce });
      fetchForces();
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.message || 'Failed to save task force';
      console.error('Error saving task force:', error);
      showToast(typeof msg === 'string' ? msg : 'Failed to save task force', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (forceId) => {
    if (!window.confirm('Delete this task force?')) return;
    try {
      await axios.delete(`${API}/task-forces/${forceId}`, { headers: getAuthHeaders() });
      fetchForces();
    } catch (error) {
      console.error('Error deleting task force:', error);
    }
  };

  const handleEdit = (force) => {
    setEditingForce(force);
    setFormData({
      ...emptyForce, ...force,
      component_list: force.component_list || [],
      personnel_roster: force.personnel_roster || []
    });
    setView('form');
  };

  const handleNew = () => {
    setEditingForce(null);
    setFormData({ ...emptyForce });
    setView('form');
  };

  const addComponent = () => {
    // Description no longer required — user can add a row with any field populated
    if (!newComponent.description.trim() && !newComponent.unit_identifier.trim() && !newComponent.quantity.trim()) return;
    setFormData(prev => ({
      ...prev,
      component_list: [...prev.component_list, { ...newComponent }]
    }));
    setNewComponent({ description: '', unit_identifier: '', quantity: '' });
  };

  const removeComponent = (idx) => {
    setFormData(prev => ({
      ...prev,
      component_list: prev.component_list.filter((_, i) => i !== idx)
    }));
  };

  const addPersonnel = () => {
    if (!newPersonnel.name.trim()) return;
    setFormData(prev => ({
      ...prev,
      personnel_roster: [...prev.personnel_roster, { ...newPersonnel }]
    }));
    setNewPersonnel({ name: '', role: '', agency: '', qualifications: '' });
  };

  const removePersonnel = (idx) => {
    setFormData(prev => ({
      ...prev,
      personnel_roster: prev.personnel_roster.filter((_, i) => i !== idx)
    }));
  };

  // Print
  const handlePrint = (force) => {
    const tfParticipants = participants.filter(p => p.taskForce === force.name || p.task_force === force.name);
    const tfResources = resources.filter(r => r.task_force === force.name);
    const componentRows = (force.component_list || []).map(c =>
      `<tr><td style="border:1px solid #555;padding:6px;">${c.description}</td><td style="border:1px solid #555;padding:6px;">${c.unit_identifier}</td><td style="border:1px solid #555;padding:6px;">${c.quantity}</td></tr>`
    ).join('');
    const personnelRows = (force.personnel_roster || []).map(p =>
      `<tr><td style="border:1px solid #555;padding:6px;">${p.name}</td><td style="border:1px solid #555;padding:6px;">${p.role}</td><td style="border:1px solid #555;padding:6px;">${p.agency}</td><td style="border:1px solid #555;padding:6px;">${p.qualifications}</td></tr>`
    ).join('');
    const assignedRows = tfParticipants.map(p =>
      `<tr><td style="border:1px solid #555;padding:6px;">${p.name || ''}</td><td style="border:1px solid #555;padding:6px;">${p.organization || ''}</td><td style="border:1px solid #555;padding:6px;">${p.qualifications || ''}</td></tr>`
    ).join('');
    const resourceRows = tfResources.map(r =>
      `<tr><td style="border:1px solid #555;padding:6px;">${r.identification || r.resource_type || ''}</td><td style="border:1px solid #555;padding:6px;">${r.description || ''}</td><td style="border:1px solid #555;padding:6px;">${r.status || ''}</td></tr>`
    ).join('');

    const printContent = `
      <html><head><title>Task Force - ${force.name}</title>
      <style>body{font-family:Arial,sans-serif;color:#333;padding:20px}h1{color:#2563eb;border-bottom:2px solid #2563eb;padding-bottom:8px}h2{color:#2563eb;margin-top:20px;font-size:16px}table{width:100%;border-collapse:collapse;margin:8px 0}th{background:#f5f5f5;border:1px solid #555;padding:6px;text-align:left;font-size:13px}td{border:1px solid #555;padding:6px;font-size:13px}.field{margin:4px 0}.label{font-weight:bold;display:inline-block;min-width:200px}.section{margin-bottom:16px;padding:12px;border:1px solid #ddd;border-radius:4px}</style></head><body>
      <h1>Task Force: ${force.name}</h1>
      <p><strong>Exercise:</strong> ${currentExercise?.exercise_name || ''}</p>
      <div class="section"><h2>1. Identification and Structure</h2>
        <div class="field"><span class="label">Task Force Identifier:</span> ${force.name}</div>
        <div class="field"><span class="label">Task Force Leader:</span> ${force.leader_name}</div>
        <div class="field"><span class="label">TFL Contact:</span> ${force.leader_contact}</div>
        <h3 style="font-size:14px;margin-top:12px;">Component List</h3>
        <table><tr><th>Description</th><th>Unit ID</th><th>Qty</th></tr>${componentRows || '<tr><td colspan="3" style="text-align:center;border:1px solid #555;padding:6px;">No components listed</td></tr>'}</table>
      </div>
      <div class="section"><h2>2. Personnel and Unit Details</h2>
        <table><tr><th>Name</th><th>Role</th><th>Agency</th><th>Qualifications</th></tr>${personnelRows || '<tr><td colspan="4" style="text-align:center;border:1px solid #555;padding:6px;">No personnel listed</td></tr>'}</table>
        ${assignedRows ? `<h3 style="font-size:14px;margin-top:12px;">Quick-Assigned Participants</h3><table><tr><th>Name</th><th>Organization</th><th>Qualifications</th></tr>${assignedRows}</table>` : ''}
        ${resourceRows ? `<h3 style="font-size:14px;margin-top:12px;">Assigned Resources</h3><table><tr><th>Identification</th><th>Description</th><th>Status</th></tr>${resourceRows}</table>` : ''}
      </div>
      <div class="section"><h2>3. Resource Status and Tracking</h2>
        <div class="field"><span class="label">Check-In Location:</span> ${force.check_in_location}</div>
        <div class="field"><span class="label">Check-In Time:</span> ${force.check_in_time}</div>
        <div class="field"><span class="label">Resource Status:</span> ${force.resource_status}</div>
        <div class="field"><span class="label">Current Location:</span> ${force.current_location}</div>
      </div>
      <div class="section"><h2>4. Operational and Tactical Data</h2>
        <div class="field"><span class="label">Operational Period:</span> ${force.operational_period_start} - ${force.operational_period_end}</div>
        <div class="field"><span class="label">Mission/Task Assignment:</span> ${force.mission_assignment}</div>
        <div class="field"><span class="label">Communications Plan:</span> ${force.comms_plan}</div>
        <div class="field"><span class="label">Safety Notes:</span> ${force.safety_notes}</div>
      </div>
      <div class="section"><h2>5. Logistics and Finance</h2>
        <div class="field"><span class="label">Requisition/Order Number:</span> ${force.requisition_order_number}</div>
        <div class="field"><span class="label">Fuel/Support Needs:</span> ${force.fuel_support_needs}</div>
        <div class="field"><span class="label">Check-Out Time:</span> ${force.check_out_time}</div>
      </div>
      </body></html>`;

    const w = window.open('', '_blank');
    writeSanitizedHTML(w.document, printContent);
    w.print();
  };

  const statusColors = {
    'Available': 'bg-green-500/20 text-green-400 border-green-500/30',
    'Assigned': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'Out of Service': 'bg-red-500/20 text-red-400 border-red-500/30'
  };

  // Sidebar data - Participants (show ALL; normalize sentinel 'none'/'N/A' as unassigned)
  const isRealAssignment = (v) => {
    const s = (v || '').toString().trim().toLowerCase();
    return s !== '' && s !== 'none' && s !== 'n/a';
  };
  const unassignedParticipants = participants;
  const assignedParticipants = participants.filter(p => isRealAssignment(p.taskForce) || isRealAssignment(p.task_force));
  const filteredUnassignedParticipants = unassignedParticipants.filter(p => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const name = (p.name || `${p.firstName || ''} ${p.lastName || ''}`).toLowerCase();
    const org = (p.organization || '').toLowerCase();
    return name.includes(q) || org.includes(q);
  });

  // Sidebar data - Resources (show ALL; dim already-assigned)
  const unassignedResources = resources;
  const assignedResources = resources.filter(r => isRealAssignment(r.task_force));
  const filteredUnassignedResources = unassignedResources.filter(r => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const name = (r.unit_name || r.resource_type || '').toLowerCase();
    const desc = (r.description || '').toLowerCase();
    return name.includes(q) || desc.includes(q);
  });

  // Sidebar data - Aircraft (from ICS 220: Fixed Wing / Helicopters / Drones)
  const assignedAircraft = aircraft.filter(a => isRealAssignment(a.task_force));
  const filteredAircraft = aircraft.filter(a => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (a.registration || '').toLowerCase().includes(q) ||
           (a.make_model || '').toLowerCase().includes(q) ||
           (a.base || '').toLowerCase().includes(q) ||
           (a.category || '').toLowerCase().includes(q);
  });

  const hasItems = forces.length > 0 && (participants.length > 0 || resources.length > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // FORM VIEW
  if (view === 'form') {
    return (
      <div className="flex flex-col h-full">
        {/* Sticky top bar */}
        <div className="sticky top-0 z-10 bg-gray-900/95 backdrop-blur-sm border-b border-gray-700 px-6 py-3 flex items-center justify-between">
          <Button variant="ghost" onClick={() => { setView('list'); setEditingForce(null); }} className="text-blue-500 hover:text-blue-400">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Task Forces
          </Button>
          <div className="flex items-center gap-3">
            <Button variant="outline" className="border-gray-600 text-gray-400" onClick={() => { setView('list'); setEditingForce(null); }}>Cancel</Button>
            <Button className="bg-blue-500 hover:bg-blue-600 text-white font-semibold" onClick={handleSave} disabled={saving || !formData.name.trim()} data-testid="save-task-force-top-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {editingForce ? 'Update Task Force' : 'Create Task Force'}
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 max-w-4xl mx-auto w-full space-y-6 pb-24">
        <h2 className="text-2xl font-bold text-white">{editingForce ? 'Edit Task Force' : 'Create Task Force'}</h2>

        {/* Section 1: Identification and Structure */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader><CardTitle className="text-blue-500 text-lg">1. Task Force Identification and Structure</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-gray-300">Task Force Identifier *</Label>
                <Input value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white" placeholder="e.g., TF-Alpha" data-testid="tf-name" />
              </div>
              <div>
                <Label className="text-gray-300">Task Force Leader (TFL)</Label>
                <Input value={formData.leader_name} onChange={(e) => setFormData(prev => ({ ...prev, leader_name: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white" placeholder="Leader name" data-testid="tf-leader-name" />
              </div>
              <div>
                <Label className="text-gray-300">TFL Contact Number</Label>
                <Input value={formData.leader_contact} onChange={(e) => setFormData(prev => ({ ...prev, leader_contact: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white" placeholder="Radio / mobile / sat phone" data-testid="tf-leader-contact" />
              </div>
            </div>

            <div>
              <Label className="text-gray-300 mb-2 block">Component List</Label>
              {formData.component_list.length > 0 && (
                <div className="overflow-x-auto mb-3">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-600">
                      <th className="text-left text-gray-400 py-2 px-3">Description</th>
                      <th className="text-left text-gray-400 py-2 px-3">Unit ID / Call Sign</th>
                      <th className="text-left text-gray-400 py-2 px-3">Qty</th>
                      <th className="text-right text-gray-400 py-2 px-3">Action</th>
                    </tr></thead>
                    <tbody>
                      {formData.component_list.map((c, idx) => (
                        <tr key={`tf-${idx}`} className="border-b border-gray-700/50">
                          <td className="py-2 px-3 text-white">{c.description}</td>
                          <td className="py-2 px-3 text-gray-300">{c.unit_identifier}</td>
                          <td className="py-2 px-3 text-gray-300">{c.quantity}</td>
                          <td className="py-2 px-3 text-right">
                            <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 h-7 w-7 p-0" onClick={() => removeComponent(idx)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                <div>
                  <Label className="text-gray-300 text-xs">Description</Label>
                  <Input value={newComponent.description} onChange={(e) => setNewComponent(prev => ({ ...prev, description: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white text-sm" placeholder="e.g., Engine" />
                </div>
                <div>
                  <Label className="text-gray-300 text-xs">Unit ID / Call Sign</Label>
                  <Input value={newComponent.unit_identifier} onChange={(e) => setNewComponent(prev => ({ ...prev, unit_identifier: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white text-sm" placeholder="e.g., Engine 101" />
                </div>
                <div>
                  <Label className="text-gray-300 text-xs">Quantity</Label>
                  <Input value={newComponent.quantity} onChange={(e) => setNewComponent(prev => ({ ...prev, quantity: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white text-sm" placeholder="e.g., 2" />
                </div>
                <Button size="sm" className="bg-blue-500 hover:bg-blue-600 text-white font-semibold" onClick={addComponent} disabled={!newComponent.description.trim() && !newComponent.unit_identifier.trim() && !newComponent.quantity.trim()} data-testid="add-component-btn">
                  <Plus className="h-3 w-3 mr-1" /> Add
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Personnel and Unit Details */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader><CardTitle className="text-blue-500 text-lg">2. Personnel and Unit Details (Accountability)</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {/* Quick-assigned participants */}
            {(() => {
              const forceName = (formData.name || '').trim();
              const tfParticipants = forceName
                ? participants.filter(p => p.taskForce === forceName || p.task_force === forceName)
                : [];
              const tfResources = forceName
                ? resources.filter(r => r.task_force === forceName)
                : [];
              return (
                <>
                  {tfParticipants.length > 0 && (
                    <div>
                      <Label className="text-gray-300 mb-2 block">Quick-Assigned Participants</Label>
                      <div className="overflow-x-auto mb-3">
                        <table className="w-full text-sm">
                          <thead><tr className="border-b border-gray-600">
                            <th className="text-left text-gray-400 py-2 px-3">Name</th>
                            <th className="text-left text-gray-400 py-2 px-3">Organization</th>
                            <th className="text-left text-gray-400 py-2 px-3">Qualifications</th>
                          </tr></thead>
                          <tbody>
                            {tfParticipants.map((p, idx) => (
                              <tr key={`tf-${idx}`} className="border-b border-gray-700/50">
                                <td className="py-2 px-3 text-white">{p.name || ''}</td>
                                <td className="py-2 px-3 text-gray-300">{p.organization || ''}</td>
                                <td className="py-2 px-3 text-gray-300">{p.qualifications || ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {tfResources.length > 0 && (
                    <div>
                      <Label className="text-gray-300 mb-2 block">Assigned Resources</Label>
                      <div className="overflow-x-auto mb-3">
                        <table className="w-full text-sm">
                          <thead><tr className="border-b border-gray-600">
                            <th className="text-left text-gray-400 py-2 px-3">Identification</th>
                            <th className="text-left text-gray-400 py-2 px-3">Type</th>
                            <th className="text-left text-gray-400 py-2 px-3">Description</th>
                            <th className="text-left text-gray-400 py-2 px-3">Status</th>
                          </tr></thead>
                          <tbody>
                            {tfResources.map((r, idx) => (
                              <tr key={`tf-${idx}`} className="border-b border-gray-700/50">
                                <td className="py-2 px-3 text-white">{r.identification || ''}</td>
                                <td className="py-2 px-3 text-gray-300">{r.resource_type || ''}</td>
                                <td className="py-2 px-3 text-gray-300">{r.description || ''}</td>
                                <td className="py-2 px-3 text-gray-300">{r.status || ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </CardContent>
        </Card>

        {/* Section 3: Resource Status and Tracking */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader><CardTitle className="text-blue-500 text-lg">3. Resource Status and Tracking</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Check-In Location</Label>
                <Input value={formData.check_in_location} onChange={(e) => setFormData(prev => ({ ...prev, check_in_location: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white" placeholder="e.g., Staging Area, Helibase" data-testid="tf-checkin-location" />
              </div>
              <div>
                <Label className="text-gray-300">Time of Check-In / Deployment</Label>
                <Input type="datetime-local" value={formData.check_in_time} onChange={(e) => setFormData(prev => ({ ...prev, check_in_time: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white" data-testid="tf-checkin-time" />
              </div>
              <div>
                <Label className="text-gray-300">Resource Status</Label>
                <Select value={formData.resource_status} onValueChange={(v) => setFormData(prev => ({ ...prev, resource_status: v }))}>
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white" data-testid="tf-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600">
                    {['Available', 'Assigned', 'Out of Service'].map(s => (
                      <SelectItem key={s} value={s} className="text-white">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300">Current Location / Assignment</Label>
                <Input value={formData.current_location} onChange={(e) => setFormData(prev => ({ ...prev, current_location: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white" placeholder="Division, group, or work location" data-testid="tf-current-location" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 4: Operational and Tactical Data */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader><CardTitle className="text-blue-500 text-lg">4. Operational and Tactical Data</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Operational Period Start</Label>
                <Input type="datetime-local" value={formData.operational_period_start} onChange={(e) => setFormData(prev => ({ ...prev, operational_period_start: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white" />
              </div>
              <div>
                <Label className="text-gray-300">Operational Period End</Label>
                <Input type="datetime-local" value={formData.operational_period_end} onChange={(e) => setFormData(prev => ({ ...prev, operational_period_end: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white" />
              </div>
            </div>
            <div>
              <Label className="text-gray-300">Mission / Task Assignment</Label>
              <Textarea value={formData.mission_assignment} onChange={(e) => setFormData(prev => ({ ...prev, mission_assignment: e.target.value }))}
                className="bg-gray-700 border-gray-600 text-white" rows={2} placeholder="Specific objective from ICS 204" data-testid="tf-mission" />
            </div>
            <div>
              <Label className="text-gray-300">Communications Plan</Label>
              <Textarea value={formData.comms_plan} onChange={(e) => setFormData(prev => ({ ...prev, comms_plan: e.target.value }))}
                className="bg-gray-700 border-gray-600 text-white" rows={2} placeholder="Radio frequencies or talk groups" />
            </div>
            <div>
              <Label className="text-gray-300">Safety Plan / Notes</Label>
              <Textarea value={formData.safety_notes} onChange={(e) => setFormData(prev => ({ ...prev, safety_notes: e.target.value }))}
                className="bg-gray-700 border-gray-600 text-white" rows={2} placeholder="Specific hazards or requirements" />
            </div>
          </CardContent>
        </Card>

        {/* Section 5: Logistics and Finance */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader><CardTitle className="text-blue-500 text-lg">5. Logistics and Finance</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-gray-300">Requisition / Order Number</Label>
                <Input value={formData.requisition_order_number} onChange={(e) => setFormData(prev => ({ ...prev, requisition_order_number: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white" placeholder="ICS 203/204 reference" data-testid="tf-requisition" />
              </div>
              <div>
                <Label className="text-gray-300">Fuel / Support Needs</Label>
                <Input value={formData.fuel_support_needs} onChange={(e) => setFormData(prev => ({ ...prev, fuel_support_needs: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white" placeholder="Fuel status, maintenance" />
              </div>
              <div>
                <Label className="text-gray-300">Check-Out Time</Label>
                <Input type="datetime-local" value={formData.check_out_time} onChange={(e) => setFormData(prev => ({ ...prev, check_out_time: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white" data-testid="tf-checkout-time" />
              </div>
            </div>
          </CardContent>
        </Card>

        </div>

        {/* Sticky bottom save bar */}
        <div className="sticky bottom-0 z-10 bg-gray-900/95 backdrop-blur-sm border-t border-gray-700 px-6 py-3 flex justify-end gap-3">
          <Button variant="outline" className="border-gray-600 text-gray-400" onClick={() => { setView('list'); setEditingForce(null); }}>Cancel</Button>
          <Button className="bg-blue-500 hover:bg-blue-600 text-white font-semibold" onClick={handleSave} disabled={saving || !formData.name.trim()} data-testid="save-task-force-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {editingForce ? 'Update Task Force' : 'Create Task Force'}
          </Button>
        </div>
      </div>
    );
  }

  // LIST VIEW with Quick Assign sidebar
  return (
    <div className="relative flex h-full">
      {/* Toast notification */}
      {toast && (
        <div className={`fixed top-4 z-50 px-4 py-3 rounded-lg shadow-lg border backdrop-blur-sm transition-all animate-in slide-in-from-top-2 ${sidebarOpen ? 'right-[21rem]' : 'right-4'} ${
          toast.type === 'error'
            ? 'bg-red-900/90 border-red-700 text-red-100'
            : 'bg-green-900/90 border-green-700 text-green-100'
        }`} data-testid="tf-assign-toast">
          <div className="flex items-center gap-2">
            {toast.type === 'error' ? <X className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
            <span className="text-sm font-medium">{toast.message}</span>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className={`flex-1 p-6 space-y-6 transition-all duration-300 ${sidebarOpen ? 'mr-80' : ''}`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Layers className="h-6 w-6 text-blue-500" />
              Task Forces
            </h2>
            <p className="text-gray-400 text-sm mt-1">{currentExercise?.exercise_name || 'No exercise selected'}</p>
          </div>
          <div className="flex items-center gap-2">
            {hasItems && (
              <Button
                variant="outline"
                className={`border-blue-500/40 text-blue-400 hover:bg-blue-500/10 ${sidebarOpen ? 'bg-blue-500/10' : ''}`}
                onClick={() => setSidebarOpen(!sidebarOpen)}
                data-testid="toggle-tf-quick-assign-btn"
              >
                {sidebarOpen ? <PanelRightClose className="h-4 w-4 mr-2" /> : <PanelRightOpen className="h-4 w-4 mr-2" />}
                Quick Assign
              </Button>
            )}
            <Button className="bg-blue-500 hover:bg-blue-600 text-white font-semibold" onClick={handleNew} data-testid="new-task-force-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Task Force
            </Button>
          </div>
        </div>

        {forces.length === 0 ? (
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="text-center py-12">
              <Layers className="h-12 w-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-300 mb-2">No Task Forces Created</h3>
              <p className="text-gray-500 text-sm mb-4">Create your first task force to manage multi-resource groups for this exercise.</p>
              <Button className="bg-blue-500 hover:bg-blue-600 text-white font-semibold" onClick={handleNew}>
                <Plus className="h-4 w-4 mr-2" /> Create Task Force
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {forces.map(force => {
              const tfMembers = participants.filter(p => p.taskForce === force.name || p.task_force === force.name);
              const tfResources = resources.filter(r => r.task_force === force.name);
              const tfAircraft = aircraft.filter(a => a.task_force === force.name);
              const isDropTarget = dropTargetId === force.id;
              return (
                <Card
                  key={force.id}
                  className={`bg-gray-800/50 border-gray-700 transition-all duration-200 ${
                    isDropTarget ? 'border-blue-500 ring-2 ring-blue-500/30 scale-[1.01]' : 'hover:border-blue-500/40'
                  }`}
                  data-testid={`task-force-card-${force.id}`}
                  onDragOver={(e) => onDragOver(e, force.id)}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => onDrop(e, force.name)}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-white">{force.name}</h3>
                        <p className="text-gray-400 text-sm">{force.mission_assignment || 'No mission assigned'}</p>
                      </div>
                      <Badge className={`${statusColors[force.resource_status] || 'bg-gray-500/20 text-gray-400'} border text-xs`}>
                        {force.resource_status}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-y-2 text-sm mb-3">
                      <div><span className="text-gray-500">Leader:</span> <span className="text-gray-300 ml-1">{force.leader_name || 'N/A'}</span></div>
                      <div><span className="text-gray-500">Location:</span> <span className="text-gray-300 ml-1">{force.current_location || 'N/A'}</span></div>
                    </div>

                    {/* Quick-glance count badges */}
                    <div className="flex flex-wrap gap-2 mb-3" data-testid={`tf-badges-${force.id}`}>
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${
                        ((force.personnel_roster || []).length + tfMembers.length) > 0 ? 'bg-blue-500/15 border-blue-500/30 text-blue-300' : 'bg-gray-800/50 border-gray-700 text-gray-500'
                      }`} data-testid={`tf-personnel-badge-${force.id}`}>
                        <Users className="h-3 w-3" />
                        {(force.personnel_roster || []).length + tfMembers.length} Personnel
                      </span>
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${
                        (force.component_list || []).length > 0 ? 'bg-indigo-500/15 border-indigo-500/30 text-indigo-300' : 'bg-gray-800/50 border-gray-700 text-gray-500'
                      }`} data-testid={`tf-components-badge-${force.id}`}>
                        <Layers className="h-3 w-3" />
                        {(force.component_list || []).length} Component{(force.component_list || []).length !== 1 ? 's' : ''}
                      </span>
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${
                        tfResources.length > 0 ? 'bg-teal-500/15 border-teal-500/30 text-teal-300' : 'bg-gray-800/50 border-gray-700 text-gray-500'
                      }`} data-testid={`tf-resources-badge-${force.id}`}>
                        <Package className="h-3 w-3" />
                        {tfResources.length} Resource{tfResources.length !== 1 ? 's' : ''}
                      </span>
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${
                        tfAircraft.length > 0 ? 'bg-sky-500/15 border-sky-500/30 text-sky-300' : 'bg-gray-800/50 border-gray-700 text-gray-500'
                      }`} data-testid={`tf-aircraft-badge-${force.id}`}>
                        <Plane className="h-3 w-3" />
                        {tfAircraft.length} Aircraft
                      </span>
                    </div>

                    {/* Assigned members inline preview */}
                    {tfMembers.length > 0 && (
                      <div className="mb-2 border border-gray-700/50 rounded-lg p-2 bg-gray-900/30">
                        <p className="text-gray-500 text-xs mb-1.5 font-medium uppercase tracking-wider flex items-center gap-1">
                          <Users className="h-3 w-3" /> Assigned Personnel
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {tfMembers.map(p => (
                            <span key={p.id} className="inline-flex items-center gap-1 bg-gray-700/60 text-gray-200 text-xs px-2 py-1 rounded-md" data-testid={`tf-assigned-member-${p.id}`}>
                              {p.name || `${p.firstName || ''} ${p.lastName || ''}`}
                              <button className="ml-0.5 text-gray-500 hover:text-red-400 transition-colors" onClick={() => unassignParticipant(p.id)} title="Unassign" data-testid={`tf-unassign-${p.id}`}>
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Assigned resources inline preview */}
                    {tfResources.length > 0 && (
                      <div className="mb-2 border border-teal-700/30 rounded-lg p-2 bg-teal-900/10">
                        <p className="text-teal-500/70 text-xs mb-1.5 font-medium uppercase tracking-wider flex items-center gap-1">
                          <Package className="h-3 w-3" /> Assigned Resources
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {tfResources.map(r => (
                            <span key={r.id} className="inline-flex items-center gap-1 bg-teal-800/30 text-teal-200 text-xs px-2 py-1 rounded-md border border-teal-700/30" data-testid={`tf-assigned-resource-${r.id}`}>
                              <Package className="h-3 w-3 text-teal-400/60" />
                              {r.unit_name || r.resource_type || 'Unit'}
                              <button className="ml-0.5 text-teal-500/50 hover:text-red-400 transition-colors" onClick={() => unassignResource(r.id)} title="Unassign" data-testid={`tf-unassign-resource-${r.id}`}>
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Assigned aircraft inline preview */}
                    {tfAircraft.length > 0 && (
                      <div className="mb-2 border border-sky-700/30 rounded-lg p-2 bg-sky-900/10">
                        <p className="text-sky-500/70 text-xs mb-1.5 font-medium uppercase tracking-wider flex items-center gap-1">
                          <Plane className="h-3 w-3" /> Assigned Aircraft
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {tfAircraft.map(a => (
                            <span key={aircraftKey(a)} className="inline-flex items-center gap-1 bg-sky-800/30 text-sky-200 text-xs px-2 py-1 rounded-md border border-sky-700/30" data-testid={`tf-assigned-aircraft-${aircraftKey(a)}`}>
                              <Plane className="h-3 w-3 text-sky-400/60" />
                              {a.registration || a.make_model || 'Aircraft'}
                              <button className="ml-0.5 text-sky-500/50 hover:text-red-400 transition-colors" onClick={() => unassignAircraft(a)} title="Unassign" data-testid={`tf-unassign-aircraft-${aircraftKey(a)}`}>
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Drop hint */}
                    {sidebarOpen && isDropTarget && (
                      <div className="mb-3 border-2 border-dashed border-blue-500/50 rounded-lg p-3 bg-blue-500/5 text-center">
                        <UserPlus className="h-5 w-5 text-blue-400 mx-auto mb-1" />
                        <p className="text-blue-400 text-xs font-medium">Drop to assign here</p>
                      </div>
                    )}

                    <div className="flex space-x-2 border-t border-gray-700 pt-3">
                      <Button size="sm" variant="outline" className="border-blue-500/40 text-blue-400 hover:bg-blue-500/10" onClick={() => handleEdit(force)} data-testid={`edit-tf-${force.id}`}>
                        <Edit className="h-3 w-3 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" className="border-green-500/40 text-green-400 hover:bg-green-500/10" onClick={() => handlePrint(force)} data-testid={`print-tf-${force.id}`}>
                        <Printer className="h-3 w-3 mr-1" /> Print
                      </Button>
                      <Button size="sm" variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10" onClick={() => handleDelete(force.id)} data-testid={`delete-tf-${force.id}`}>
                        <Trash2 className="h-3 w-3 mr-1" /> Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Quick Assign Sidebar */}
      <div
        className={`fixed right-0 top-0 h-full w-80 bg-gray-900 border-l border-gray-700 shadow-2xl z-40 transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="tf-quick-assign-sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 border-b border-gray-700 bg-gray-900/95 backdrop-blur-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <UserPlus className="h-4 w-4 text-blue-500" />
                Quick Assign
              </h3>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-white" onClick={() => setSidebarOpen(false)} data-testid="close-tf-quick-assign-btn">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-gray-500 text-xs mb-3">Drag onto a Task Force card to assign or reassign.</p>

            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search participants or units..."
                className="pl-8 bg-gray-800 border-gray-600 text-white text-sm h-8"
                data-testid="tf-quick-assign-search"
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
              <>
                {/* Participants Section - shows ALL (dimmed if already assigned) */}
                <div className="p-3 border-b border-gray-700/50">
                  <p className="text-blue-500/70 text-xs font-medium uppercase tracking-wider mb-2">
                    Participants ({filteredUnassignedParticipants.length})
                  </p>
                  {filteredUnassignedParticipants.length === 0 ? (
                    <div className="text-center py-6">
                      <Users className="h-8 w-8 text-gray-600 mx-auto mb-2" />
                      <p className="text-gray-500 text-xs">{searchQuery ? 'No matches' : 'No participants in this exercise'}</p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {filteredUnassignedParticipants.map(p => {
                        const rawForce = p.taskForce || p.task_force || '';
                        const isAssigned = isRealAssignment(rawForce);
                        const currentForce = isAssigned ? rawForce : '';
                        const displayName = p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Unnamed';
                        return (
                          <div
                            key={p.id}
                            draggable
                            onDragStart={(e) => onDragStart(e, p, 'participant')}
                            className={`flex items-center gap-2 px-2.5 py-2 rounded-md border cursor-grab active:cursor-grabbing transition-colors group ${
                              isAssigned
                                ? 'bg-gray-800/20 border-gray-700/30 opacity-50 hover:opacity-90'
                                : 'bg-gray-800/50 border-gray-700/50 hover:border-blue-500/30 hover:bg-gray-800'
                            }`}
                            data-testid={`tf-draggable-participant-${p.id}`}
                          >
                            <GripVertical className={`h-3.5 w-3.5 flex-shrink-0 ${isAssigned ? 'text-gray-700' : 'text-gray-600 group-hover:text-blue-500/60'}`} />
                            <Users className="h-3.5 w-3.5 text-blue-500/40 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p className={`text-sm truncate ${isAssigned ? 'text-gray-400' : 'text-white'}`}>{displayName}</p>
                              <p className="text-xs truncate">
                                {isAssigned ? (
                                  <span className="text-blue-300">Currently: {currentForce}</span>
                                ) : (
                                  <span className="text-gray-500">{p.position || p.organization || 'Unassigned'}</span>
                                )}
                              </p>
                            </div>
                            {isAssigned && (
                              <button
                                className="text-gray-600 hover:text-red-400 transition-colors p-0.5 opacity-0 group-hover:opacity-100"
                                onClick={() => unassignParticipant(p.id)}
                                title="Remove from task force"
                                data-testid={`tf-sidebar-unassign-participant-${p.id}`}
                              >
                                <UserMinus className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {assigningId === p.id && <Loader2 className="h-3 w-3 animate-spin text-blue-500 flex-shrink-0" />}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {/* Resources Section - shows ALL (dimmed if already assigned) */}
                <div className="p-3 border-b border-gray-700/50">
                  <p className="text-teal-500/70 text-xs font-medium uppercase tracking-wider mb-2">
                    Resources ({filteredUnassignedResources.length})
                  </p>
                  {filteredUnassignedResources.length === 0 ? (
                    <div className="text-center py-6">
                      <Package className="h-8 w-8 text-gray-600 mx-auto mb-2" />
                      <p className="text-gray-500 text-xs">{searchQuery ? 'No matches' : 'No resources in this exercise'}</p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {filteredUnassignedResources.map(r => {
                        const rawForce = r.task_force || '';
                        const isAssigned = isRealAssignment(rawForce);
                        return (
                          <div key={r.id} draggable onDragStart={(e) => onDragStart(e, r, 'resource')}
                            className={`flex items-center gap-2 px-2.5 py-2 rounded-md border cursor-grab active:cursor-grabbing transition-colors group ${
                              isAssigned
                                ? 'bg-gray-800/20 border-gray-700/30 opacity-50 hover:opacity-90'
                                : 'bg-gray-800/50 border-gray-700/50 hover:border-teal-500/30 hover:bg-gray-800'
                            }`}
                            data-testid={`tf-draggable-resource-${r.id}`}
                          >
                            <GripVertical className={`h-3.5 w-3.5 flex-shrink-0 ${isAssigned ? 'text-gray-700' : 'text-gray-600 group-hover:text-teal-500/60'}`} />
                            <Package className="h-3.5 w-3.5 text-teal-500/40 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p className={`text-sm truncate ${isAssigned ? 'text-gray-400' : 'text-white'}`}>{r.unit_name || 'Unnamed'}</p>
                              <p className="text-xs truncate">
                                {isAssigned ? (
                                  <span className="text-teal-300">Currently: {rawForce}</span>
                                ) : (
                                  <span className="text-gray-500">{r.resource_type || ''}</span>
                                )}
                              </p>
                            </div>
                            {!isAssigned && <Badge className={`text-[10px] px-1.5 py-0 ${statusBadgeClass(r.status)}`}>{r.status}</Badge>}
                            {isAssigned && (
                              <button className="text-gray-600 hover:text-red-400 transition-colors p-0.5 opacity-0 group-hover:opacity-100"
                                onClick={() => unassignResource(r.id)} title="Unassign" data-testid={`tf-sidebar-unassign-resource-${r.id}`}>
                                <UserMinus className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {assigningId === r.id && <Loader2 className="h-3 w-3 animate-spin text-teal-500 flex-shrink-0" />}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {/* Aircraft Section - Fixed Wing / Helicopters / Drones from ICS 220 */}
                <div className="p-3">
                  <p className="text-sky-500/70 text-xs font-medium uppercase tracking-wider mb-2">
                    Aircraft ({filteredAircraft.length})
                  </p>
                  {filteredAircraft.length === 0 ? (
                    <div className="text-center py-6">
                      <Plane className="h-8 w-8 text-gray-600 mx-auto mb-2" />
                      <p className="text-gray-500 text-xs">{searchQuery ? 'No matches' : 'No aircraft in ICS 220 for this exercise'}</p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {filteredAircraft.map(a => {
                        const rawForce = a.task_force || '';
                        const isAssigned = isRealAssignment(rawForce);
                        const key = aircraftKey(a);
                        const catLabel = a.category === 'fixed_wing' ? 'Fixed Wing' : a.category === 'helicopter' ? 'Helicopter' : 'Drone';
                        return (
                          <div key={key} draggable onDragStart={(e) => onDragStart(e, a, 'aircraft')}
                            className={`flex items-center gap-2 px-2.5 py-2 rounded-md border cursor-grab active:cursor-grabbing transition-colors group ${
                              isAssigned
                                ? 'bg-gray-800/20 border-gray-700/30 opacity-50 hover:opacity-90'
                                : 'bg-gray-800/50 border-gray-700/50 hover:border-sky-500/30 hover:bg-gray-800'
                            }`}
                            data-testid={`tf-draggable-aircraft-${key}`}
                          >
                            <GripVertical className={`h-3.5 w-3.5 flex-shrink-0 ${isAssigned ? 'text-gray-700' : 'text-gray-600 group-hover:text-sky-500/60'}`} />
                            <Plane className="h-3.5 w-3.5 text-sky-500/40 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p className={`text-sm truncate ${isAssigned ? 'text-gray-400' : 'text-white'}`}>{a.registration || a.make_model || 'Unnamed'}</p>
                              <p className="text-xs truncate">
                                {isAssigned ? (
                                  <span className="text-sky-300">Currently: {rawForce}</span>
                                ) : (
                                  <span className="text-gray-500">{catLabel}{a.make_model ? ` · ${a.make_model}` : ''}</span>
                                )}
                              </p>
                            </div>
                            {isAssigned && (
                              <button className="text-gray-600 hover:text-red-400 transition-colors p-0.5 opacity-0 group-hover:opacity-100"
                                onClick={() => unassignAircraft(a)} title="Remove from task force" data-testid={`tf-sidebar-unassign-aircraft-${key}`}>
                                <UserMinus className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {assigningId === key && <Loader2 className="h-3 w-3 animate-spin text-sky-500 flex-shrink-0" />}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
          </div>

          {/* Footer summary */}
          <div className="p-3 border-t border-gray-700 bg-gray-900/95">
            <div className="flex justify-between text-xs text-gray-500">
              <span>{participants.length}p · {resources.length}r · {aircraft.length}a</span>
              <span>{assignedParticipants.length + assignedResources.length + assignedAircraft.length} assigned</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskForcesModule;
